import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { socketService } from "../../services/socketService";
import { useCan } from "../../auth/Can";
import { getSosEvents } from "../../services/safetyService";

/** Safety alerts on every dashboard page: new SOS, ladder steps, chat safety flags, until dismissed or the SOS is
 *  acknowledged / resolved. Hidden without support:read or sos:respond. */

export interface SafetyAlert { key: string; sosEventId?: number; text: string; to: string; action: string }
const MAX_ALERTS = 5;
/** R4-A5: SOS alerts are pinned on top (up to 20); other alerts fill at most 5 slots and never push an SOS off. */
export const MAX_SOS_ALERTS = 20;
export const arrangeAlerts = (list: SafetyAlert[]): SafetyAlert[] => {
    const sos = list.filter((x) => x.sosEventId != null).slice(0, MAX_SOS_ALERTS);
    return [...sos, ...list.filter((x) => x.sosEventId == null).slice(0, MAX_ALERTS)];
};

export const alertFromEvent = (event: string, p: any): SafetyAlert | null => {
    if (event === "safety:sos" && p?.sosEventId != null) {
        // R4-S3: the rider pressed SOS again on an SOS still open.
        const what = p.again ? "the rider pressed SOS again" : "a rider needs help now";
        return { key: `sos-${p.sosEventId}`, sosEventId: p.sosEventId, text: `🚨 ${p.again ? "" : "New "}SOS #${p.sosEventId}${p.publicNumber ? ` (${p.publicNumber})` : ""} — ${what}`, to: "/safety-sos", action: "Open SOS console" };
    }
    if (event === "safety:sos:escalation" && p?.sosEventId != null) {
        return { key: `sos-${p.sosEventId}`, sosEventId: p.sosEventId, text: `🚨 SOS #${p.sosEventId} not acknowledged for ${p.minutes} min — escalated${p.stage >= 2 ? " (on-call texted)" : ""}`, to: "/safety-sos", action: "Acknowledge" };
    }
    if (event === "support:mvagOverdue" && p?.ticketId) {
        const what = p.kind === "decision" ? "Suspend decision overdue" : "Driver-misconduct inquiry overdue";
        return { key: `mvag-${p.kind}-${p.ticketId}`, text: `⏰ ${what} on ${p.publicNumber || `ticket #${p.ticketId}`}`, to: `/support-ticket/${p.ticketId}`, action: "Open ticket" };
    }
    if (event === "support:escalated" && p?.ticketId) {
        return { key: `esc-${p.ticketId}`, text: `⬆️ ${p.publicNumber || `Ticket #${p.ticketId}`} escalated to ${p.tier}${p.reason ? ` (${String(p.reason).replace(/_/g, " ")})` : ""}`, to: `/support-ticket/${p.ticketId}`, action: "Open ticket" };
    }
    if (event === "support:approvalRequested" && p?.ticketId) {
        return { key: `appr-${p.approvalId}`, text: `✋ Approval needed on ${p.publicNumber || `ticket #${p.ticketId}`}: ${String(p.kind).replace(/_/g, " ")}${p.amount != null ? ` ₹${p.amount}` : ""}`, to: `/support-ticket/${p.ticketId}`, action: "Open ticket" };
    }
    if (event === "support:rideAlert" && p?.ticketId) {
        const what = p.kind === "long_stop" ? `stopped for ${p.minutes} min` : `${p.offKm} km off route`;
        return { key: `ride-${p.rideId}-${p.kind}`, text: `🛑 Ride #${p.rideId} ${what} — rider asked "Are you okay?" (${p.publicNumber || `ticket #${p.ticketId}`})`, to: `/support-ticket/${p.ticketId}`, action: "Open ticket" };
    }
    if (event === "support:safetyEscalation") {
        const where = p?.publicNumber || (p?.chatRoomId != null ? `chat ${p.chatRoomId}` : "a chat");
        return { key: `chat-${p?.ticketId ?? p?.chatRoomId ?? "x"}`, text: `⚠️ Safety words in ${where} — treat as P0`, to: p?.ticketId ? `/support-ticket/${p.ticketId}` : "/support-tickets", action: "Open ticket" };
    }
    return null;
};

const SafetyAlertBar: React.FC = () => {
    const { can } = useCan();
    const sosTeam = can("sos:respond" as any); // owner (13 Oct): SOS alerts + the console are for the SOS team
    const allowed = can("support:read") || sosTeam; // R3 FM4: SOS-only staff get the bar too
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState<SafetyAlert[]>([]);
    const alertsRef = useRef<SafetyAlert[]>([]);
    alertsRef.current = alerts;

    useEffect(() => {
        if (!allowed) return;
        socketService.connect();
        const add = (event: string) => (p: any) => {
            const a = alertFromEvent(event, p);
            if (a) setAlerts((cur) => arrangeAlerts([a, ...cur.filter((x) => x.key !== a.key)]));
        };
        // Someone acknowledged or resolved it → the alert goes away everywhere.
        const onUpdate = (p: any) => {
            if (p?.acknowledgedAt || (p?.status && p.status !== "ACTIVE")) setAlerts((cur) => cur.filter((x) => x.sosEventId !== p.sosEventId));
        };
        // R2 D8 + R3 FM5: SOS nobody acknowledged yet show on open and again after every (re)connect.
        // R4-A5: the server's list replaces the SOS alerts that were on the bar when it was asked (one acknowledged while we
        // were away goes); an SOS that arrived by socket meanwhile stays.
        const preload = () => {
            if (!sosTeam) return;
            const asked = new Set(alertsRef.current.filter((x) => x.sosEventId != null).map((x) => x.sosEventId));
            getSosEvents({ status: "ACTIVE", type: "SOS", unacked: 1, limit: MAX_SOS_ALERTS })
                .then((r) => {
                    const fresh = ((r?.data?.events ?? []) as any[]).filter((ev) => !ev.acknowledgedAt)
                        .map((ev) => alertFromEvent("safety:sos", { ...ev, sosEventId: ev.id })).filter((a): a is SafetyAlert => !!a);
                    const freshIds = new Set(fresh.map((a) => a.sosEventId));
                    setAlerts((cur) => arrangeAlerts([
                        ...cur.filter((x) => x.sosEventId != null && !asked.has(x.sosEventId) && !freshIds.has(x.sosEventId)),
                        ...fresh.map((a) => cur.find((x) => x.key === a.key) ?? a),
                        ...cur.filter((x) => x.sosEventId == null),
                    ]));
                })
                .catch(() => undefined);
        };
        const handlers: [string, (p: any) => void][] = [
            ["connect", preload],
            ...(sosTeam ? [["safety:sos", add("safety:sos")], ["safety:sos:escalation", add("safety:sos:escalation")]] as [string, (p: any) => void][] : []),
            ["support:safetyEscalation", add("support:safetyEscalation")],
            ["support:mvagOverdue", add("support:mvagOverdue")],
            ["support:escalated", add("support:escalated")],
            ["support:approvalRequested", add("support:approvalRequested")],
            ["support:rideAlert", add("support:rideAlert")],
            ["safety:sos:update", onUpdate],
        ];
        handlers.forEach(([e, fn]) => socketService.on(e, fn));
        preload();
        return () => handlers.forEach(([e, fn]) => socketService.off(e, fn));
    }, [allowed, sosTeam]);

    if (!allowed || !alerts.length) return null;
    return (
        <Stack spacing={1} sx={{ width: "100%", mb: 2 }} data-testid="safety-alert-bar">
            {alerts.map((a) => (
                <Alert
                    key={a.key}
                    severity="error"
                    variant="filled"
                    action={
                        <>
                            <Button color="inherit" size="small" onClick={() => navigate(a.to)}>{a.action}</Button>
                            <Button color="inherit" size="small" aria-label="Dismiss" onClick={() => setAlerts((cur) => cur.filter((x) => x.key !== a.key))}>✕</Button>
                        </>
                    }
                >
                    {a.text}
                </Alert>
            ))}
        </Stack>
    );
};

export default SafetyAlertBar;
